import { createSelector, createSlice, PayloadAction, SerializedError } from "@reduxjs/toolkit";
import { current, isDraft, type Draft } from "immer";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import Session from "users/__session/session";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import { proposalsApi } from "work/proposals/_api/proposal-api";
import ProposalQuerySchema, { ProposalQueryJSON } from "work/proposals/_api/queries/_schemas/proposal-query-schema";
import ProposalQuery from "work/proposals/_api/queries/proposal-query";
import BarebonesProposalResponse from "work/proposals/_api/responses/barebones-proposal-response";
import { extractProposalLocalStoreState } from "work/proposals/_store/proposal-local-store";
import { incomingProposalRequiresDraftBuilderRebase } from "work/proposals/_store/proposal-server-state";
import { ProposalBuilderJSON } from "work/proposals/_schemas/proposal-builder-schema";
import ProposalSchema, { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import { ProposalStatus } from "work/proposals/__constants/proposal-lifecycle";
import Proposal from "work/proposals/proposal";
import ProposalRedlineFactory, {
  type LocalProposalJSON,
  type ProposalCounterRevisionState,
} from "work/proposals/redlining/__factory/proposal-redline-factory";
import { cleanupWorkDocumentWrappers } from "work/work-document-cache-cleanup";

export type ProposalLoadIssue = { id: Guid; issue: string };
export const isProposalLoadIssue = (value: object): value is ProposalLoadIssue => {
  return "issue" in value;
};

type LatestRevisionRedirect = {
  previousLatestRevisionId: string;
  latestRevisionId: string;
  supersededProposalId?: string;
};

type ProposalDraftBuilderEntry = {
  original?: ProposalBuilderJSON;
  updated?: ProposalBuilderJSON;
};

type ProposalStoreEntry = {
  barebones?: BarebonesProposalResponse;
  draftBuilder?: ProposalDraftBuilderEntry;
  /**
   * The proposal is gone for this viewer — deleted, or their access to it revoked.
   *
   * Clearing the caches is not enough on its own to get someone out of a proposal they have open: the
   * editor renders from the canonical negotiation envelope, which keeps its last good projection, so a
   * reviewer whose proposal was deleted under them went on editing a document that no longer exists.
   * This is the durable fact the open pages watch, so a realtime deletion ejects them exactly as
   * opening a deleted proposal does.
   */
  isNoLongerAvailable?: boolean;
  localRevision?: ProposalCounterRevisionState;
  localVersion?: ProposalJSON;
  redirects?: {
    latestRevision?: LatestRevisionRedirect;
  };
  serverVersion?: ProposalJSON | null;
};

type ProposalQueryEntry = {
  interfaces: string[];
  lastSuccessfulResponse?: PaginatedResponse<ProposalJSON | ProposalLoadIssue>;
  query?: ProposalQueryJSON;
  response?: PaginatedResponse<ProposalJSON | ProposalLoadIssue>;
};

type DraftProposalStoreState = Draft<ProposalStoreState>;
type DraftProposalStoreEntry = Draft<ProposalStoreEntry>;
type DraftProposalQueryEntry = Draft<ProposalQueryEntry>;
type DraftPaginatedProposalResponse = Draft<PaginatedResponse<ProposalJSON | ProposalLoadIssue>>;
type ProposalEntriesById = Record<string, ProposalStoreEntry | DraftProposalStoreEntry | undefined>;
type CachedProposalJSON = ProposalJSON | Draft<ProposalJSON>;
type CachedProposalBuilderJSON = ProposalBuilderJSON | Draft<ProposalBuilderJSON>;
type CachedProposalCounterRevisionState = ProposalCounterRevisionState | Draft<ProposalCounterRevisionState>;

export type ProposalStoreState = {
  byId: Record<string, ProposalStoreEntry | undefined>;
  byQuery: Record<string, ProposalQueryEntry | undefined>;
};
const initialState: ProposalStoreState = {
  byId: {},
  byQuery: {},
};

const getOrCreateProposalEntry = (state: DraftProposalStoreState, proposalIdValue: string): DraftProposalStoreEntry => {
  const existingEntry = state.byId[proposalIdValue];
  if (existingEntry) return existingEntry;

  const entry: ProposalStoreEntry = {};
  state.byId[proposalIdValue] = entry;
  return state.byId[proposalIdValue]!;
};

const getOrCreateDraftBuilderEntry = (
  state: DraftProposalStoreState,
  proposalIdValue: string,
): Draft<ProposalDraftBuilderEntry> => {
  const proposalEntry = getOrCreateProposalEntry(state, proposalIdValue);
  if (proposalEntry.draftBuilder) return proposalEntry.draftBuilder;

  const draftBuilder: ProposalDraftBuilderEntry = {};
  proposalEntry.draftBuilder = draftBuilder;
  return proposalEntry.draftBuilder;
};

const getOrCreateQueryEntry = (state: DraftProposalStoreState, queryKey: string): DraftProposalQueryEntry => {
  const existingEntry = state.byQuery[queryKey];
  if (existingEntry) return existingEntry;

  const entry: ProposalQueryEntry = { interfaces: [] };
  state.byQuery[queryKey] = entry;
  return state.byQuery[queryKey]!;
};

const removeInterfaceFromQueryEntries = (state: DraftProposalStoreState, interfaceName: string) => {
  for (const queryEntry of Object.values(state.byQuery)) {
    if (!queryEntry) continue;
    queryEntry.interfaces = queryEntry.interfaces.filter((entryInterfaceName) => entryInterfaceName !== interfaceName);
  }
};

const addInterfaceToQueryEntry = (queryEntry: DraftProposalQueryEntry, interfaceName: string) => {
  if (queryEntry.interfaces.includes(interfaceName)) return;
  queryEntry.interfaces.push(interfaceName);
};

const clearProposalQueryResponses = (state: DraftProposalStoreState) => {
  for (const [queryKey, queryEntry] of Object.entries(state.byQuery)) {
    if (!queryEntry) continue;
    queryEntry.response = undefined;
    queryEntry.lastSuccessfulResponse = undefined;
    if (!queryEntry.query && queryEntry.interfaces.length === 0) {
      delete state.byQuery[queryKey];
    }
  }
};

const cloneProposalJSON = (proposalJSON: ProposalJSON): ProposalJSON => ({
  ...proposalJSON,
  negotiationOperations: proposalJSON.negotiationOperations?.map((operation) => ({ ...operation })),
  clientPolicies: proposalJSON.clientPolicies
    ? {
        ...proposalJSON.clientPolicies,
        documents: proposalJSON.clientPolicies.documents?.map((document) => ({
          ...document,
        })),
      }
    : proposalJSON.clientPolicies,
  vendorPolicies: proposalJSON.vendorPolicies
    ? {
        ...proposalJSON.vendorPolicies,
        documents: proposalJSON.vendorPolicies.documents?.map((document) => ({
          ...document,
        })),
      }
    : proposalJSON.vendorPolicies,
  conflicts: proposalJSON.conflicts
    ? {
        ...proposalJSON.conflicts,
        documents: proposalJSON.conflicts.documents?.map((document) => ({
          ...document,
        })),
      }
    : proposalJSON.conflicts,
  partyRevision: proposalJSON.partyRevision
    ? cloneProposalJSON(proposalJSON.partyRevision)
    : proposalJSON.partyRevision,
  supersedes: proposalJSON.supersedes ? cloneProposalJSON(proposalJSON.supersedes) : proposalJSON.supersedes,
});

const unwrapCachedProposalJSON = (proposalJSON: CachedProposalJSON | null | undefined): ProposalJSON | undefined => {
  if (!proposalJSON) return undefined;
  return isDraft(proposalJSON) ? (current(proposalJSON) as ProposalJSON) : proposalJSON;
};

const unwrapCachedProposalCounterRevisionState = (
  counterRevisionState: CachedProposalCounterRevisionState | undefined,
): ProposalCounterRevisionState | undefined => {
  if (!counterRevisionState) return undefined;
  return isDraft(counterRevisionState)
    ? (current(counterRevisionState) as ProposalCounterRevisionState)
    : counterRevisionState;
};

const unwrapCachedProposalBuilderJSON = (
  builderJSON: CachedProposalBuilderJSON | undefined,
): ProposalBuilderJSON | undefined => {
  if (!builderJSON) return undefined;
  return isDraft(builderJSON) ? (current(builderJSON) as ProposalBuilderJSON) : builderJSON;
};

const getSupersededProposalIdValuesFromProposalJSON = (proposalJSON: ProposalJSON): string[] => {
  if (proposalJSON.previousLatestRevisionId) {
    return proposalJSON.previousLatestRevisionId === proposalJSON.id ? [] : [proposalJSON.previousLatestRevisionId];
  }

  return Array.from(
    new Set(
      [proposalJSON.supersedes?.id, proposalJSON.supersedes?.latestRevisionId].filter(
        (idValue): idValue is string => Boolean(idValue) && idValue !== proposalJSON.id,
      ),
    ),
  );
};

const proposalJSONReferencesRevisionChain = ({
  latestProposalIdValue,
  proposalJSON,
  supersededProposalIdValue,
}: {
  latestProposalIdValue?: string;
  proposalJSON: ProposalJSON | undefined;
  supersededProposalIdValue: string;
}): boolean => {
  if (!proposalJSON) return false;
  if (latestProposalIdValue && proposalJSON.id === latestProposalIdValue) return false;

  return (
    proposalJSON.id === supersededProposalIdValue ||
    proposalJSON.supersedes?.id === supersededProposalIdValue ||
    Boolean(
      latestProposalIdValue &&
      (proposalJSON.supersededById === latestProposalIdValue ||
        proposalJSON.latestRevisionId === latestProposalIdValue),
    )
  );
};

const proposalEntryReferencesRevisionChain = ({
  latestProposalIdValue,
  proposalEntry,
  supersededProposalIdValue,
}: {
  latestProposalIdValue?: string;
  proposalEntry: ProposalStoreEntry | DraftProposalStoreEntry | undefined;
  supersededProposalIdValue: string;
}): boolean => {
  const redirect = proposalEntry?.redirects?.latestRevision;
  if (
    latestProposalIdValue &&
    redirect?.latestRevisionId === latestProposalIdValue &&
    (redirect.supersededProposalId === supersededProposalIdValue ||
      redirect.previousLatestRevisionId === supersededProposalIdValue)
  ) {
    return true;
  }

  return (
    proposalJSONReferencesRevisionChain({
      latestProposalIdValue,
      proposalJSON: unwrapCachedProposalJSON(proposalEntry?.serverVersion),
      supersededProposalIdValue,
    }) ||
    proposalJSONReferencesRevisionChain({
      latestProposalIdValue,
      proposalJSON: unwrapCachedProposalJSON(proposalEntry?.localVersion),
      supersededProposalIdValue,
    })
  );
};

const proposalEntryReferencesLatestRevisionId = ({
  latestProposalIdValue,
  proposalEntry,
}: {
  latestProposalIdValue: string;
  proposalEntry: ProposalStoreEntry | DraftProposalStoreEntry | undefined;
}): boolean => {
  const entryProposalJSONs = [
    unwrapCachedProposalJSON(proposalEntry?.serverVersion),
    unwrapCachedProposalJSON(proposalEntry?.localVersion),
  ];

  return entryProposalJSONs.some(
    (proposalJSON) =>
      proposalJSON?.id !== latestProposalIdValue &&
      (proposalJSON?.latestRevisionId === latestProposalIdValue ||
        proposalJSON?.supersededById === latestProposalIdValue),
  );
};

const getProposalEntryPartyRevisionSources = (
  proposalEntry: ProposalStoreEntry | DraftProposalStoreEntry | undefined,
): ProposalJSON[] => {
  const sources: ProposalJSON[] = [];
  const serverVersion = unwrapCachedProposalJSON(proposalEntry?.serverVersion);
  if (serverVersion?.partyRevision) {
    sources.push(serverVersion);
  }

  const localVersion = unwrapCachedProposalJSON(proposalEntry?.localVersion);
  if (localVersion?.partyRevision) {
    const localVersionSource: LocalProposalJSON = cloneProposalJSON(localVersion);
    localVersionSource.hasLocallyMaterializedCounterRevision = true;
    sources.push(localVersionSource);
  }

  const localRevision = unwrapCachedProposalCounterRevisionState(proposalEntry?.localRevision);
  const localRevisionMaterializationBase = serverVersion ?? localRevision?.baseProposalJSON;
  if (localRevisionMaterializationBase && localRevision) {
    const { localProposalJSON } = ProposalRedlineFactory.reconcileCounterRevisionProposalJSON({
      latestProposalJSON: localRevisionMaterializationBase,
      existingCounterRevisionState: localRevision,
    });
    if (localProposalJSON?.partyRevision) {
      sources.push(localProposalJSON);
    }
  }

  return sources;
};

const proposalRevisionId = (proposalJSON: ProposalJSON): string | undefined =>
  proposalJSON.id ?? proposalJSON.latestRevisionId ?? undefined;

const proposalHasCurrentRevisionNegotiationOperations = (proposalJSON: ProposalJSON): boolean => {
  const revisionId = proposalRevisionId(proposalJSON);
  return Boolean(
    revisionId &&
    proposalJSON.negotiationOperations?.some(
      (operation) =>
        operation.baseRevisionId === revisionId &&
        (operation.kind === "PartyRevisionPatched" || operation.kind === "PartyRevisionReplaced"),
    ),
  );
};

const proposalPartyRevisionIsKnownCurrent = (proposalJSON: ProposalJSON): boolean => {
  if (!proposalJSON.partyRevision) return false;
  if (!proposalJSON.supersedes) return true;
  return proposalHasCurrentRevisionNegotiationOperations(proposalJSON);
};

const getPartyRevisionRebaseSources = (
  proposalJSON: ProposalJSON,
  entriesById: ProposalEntriesById,
): ProposalJSON[] | undefined => {
  const sources: ProposalJSON[] = [];
  const sourceEntryIds = new Set<string>();
  const hasImmutablePredecessor = Boolean(
    proposalJSON.previousLatestRevisionId && proposalJSON.previousLatestRevisionId !== proposalJSON.id,
  );
  const addSourcesForEntry = (
    entryIdValue: string | undefined,
    proposalEntry: ProposalStoreEntry | DraftProposalStoreEntry | undefined,
  ) => {
    if (!entryIdValue || sourceEntryIds.has(entryIdValue)) return;

    sourceEntryIds.add(entryIdValue);
    sources.push(...getProposalEntryPartyRevisionSources(proposalEntry));
  };

  for (const supersededProposalIdValue of getSupersededProposalIdValuesFromProposalJSON(proposalJSON)) {
    addSourcesForEntry(supersededProposalIdValue, entriesById[supersededProposalIdValue]);
  }

  if (!hasImmutablePredecessor && proposalJSON.supersedes?.id) {
    const latestProposalIdValue = proposalJSON.id ?? undefined;
    for (const [entryIdValue, proposalEntry] of Object.entries(entriesById)) {
      if (
        proposalEntryReferencesRevisionChain({
          latestProposalIdValue,
          proposalEntry,
          supersededProposalIdValue: proposalJSON.supersedes.id,
        })
      ) {
        addSourcesForEntry(entryIdValue, proposalEntry);
      }
    }
  }

  if (!hasImmutablePredecessor && proposalJSON.id) {
    for (const [entryIdValue, proposalEntry] of Object.entries(entriesById)) {
      if (
        proposalEntry?.redirects?.latestRevision?.latestRevisionId === proposalJSON.id ||
        proposalEntryReferencesLatestRevisionId({
          latestProposalIdValue: proposalJSON.id,
          proposalEntry,
        })
      ) {
        addSourcesForEntry(entryIdValue, proposalEntry);
      }
    }
  }

  if (proposalJSON.id) {
    sources.push(
      ...getProposalEntryPartyRevisionSources(entriesById[proposalJSON.id]).filter(proposalPartyRevisionIsKnownCurrent),
    );
  }

  return sources.length > 0 ? sources : undefined;
};

const cloneProposalBuilderJSON = (builderJSON: ProposalBuilderJSON): ProposalBuilderJSON => ({
  ...builderJSON,
  specStack: builderJSON.specStack.map(cloneProposalJSON),
  sessionHistory: [...(builderJSON.sessionHistory ?? [])],
});

const cleanupProposalJSONForDeletedDocument = (
  proposalJSON: ProposalJSON | undefined | null,
  documentId: string,
  forceMode?: "remove" | "markUnavailable",
): boolean => {
  if (!proposalJSON) return false;
  const mode = forceMode ?? (proposalJSON.status === ProposalStatus.AwaitingSubmission ? "remove" : "markUnavailable");
  const currentChanged = cleanupWorkDocumentWrappers(proposalJSON, documentId, mode);
  const partyRevisionChanged = cleanupProposalJSONForDeletedDocument(proposalJSON.partyRevision, documentId, forceMode);
  const supersedesChanged = cleanupProposalJSONForDeletedDocument(proposalJSON.supersedes, documentId, forceMode);
  return currentChanged || partyRevisionChanged || supersedesChanged;
};

const cleanupProposalBuilderJSONForDeletedDocument = (
  builderJSON: ProposalBuilderJSON | undefined,
  documentId: string,
): boolean => {
  if (!builderJSON) return false;
  let changed = false;
  for (const proposalJSON of builderJSON.specStack) {
    changed = cleanupProposalJSONForDeletedDocument(proposalJSON, documentId, "remove") || changed;
  }
  return changed;
};

const cleanupProposalResponseForDeletedDocument = (
  response: PaginatedResponse<ProposalJSON | ProposalLoadIssue> | DraftPaginatedProposalResponse,
  documentId: string,
) => {
  let changed = false;
  const data = response.data.map((proposalJSON) => {
    if (isProposalLoadIssue(proposalJSON)) return proposalJSON;
    const clonedProposalJSON = cloneProposalJSON(proposalJSON);
    if (!cleanupProposalJSONForDeletedDocument(clonedProposalJSON, documentId)) return proposalJSON;
    changed = true;
    return clonedProposalJSON;
  });

  return changed ? PaginatedResponse.from(data, response.headers) : response;
};

const proposalJSONLastUpdatedIsAfter = (
  proposalJSON: ProposalJSON | Draft<ProposalJSON> | null | undefined,
  date?: AHDate | null,
): boolean => {
  if (!proposalJSON?.lastUpdated || !date) return false;

  try {
    const comparisonDate = AHDate.fromJSON(date.toISOString());
    return Boolean(comparisonDate && AHDate.fromJSON(proposalJSON.lastUpdated)?.isAfter(comparisonDate));
  } catch {
    return false;
  }
};

const cacheProposalServerVersion = (state: DraftProposalStoreState, proposalJSON: ProposalJSON): ProposalJSON => {
  const proposalJSONWithRedirect = applyLatestRevisionRedirectToProposalJSON(proposalJSON, state.byId);
  if (!proposalJSONWithRedirect.id) return proposalJSONWithRedirect;

  const proposalEntry = getOrCreateProposalEntry(state, proposalJSONWithRedirect.id);
  const localVersion = unwrapCachedProposalJSON(proposalEntry.localVersion);
  const partyRevisionSourceProposalJSON = [
    ...(getPartyRevisionRebaseSources(proposalJSONWithRedirect, state.byId) ?? []),
    ...(proposalPartyRevisionIsKnownCurrent(proposalJSONWithRedirect) ? [proposalJSONWithRedirect] : []),
  ];
  proposalEntry.serverVersion = proposalJSONWithRedirect;
  const counterRevisionReconciliation = ProposalRedlineFactory.reconcileCounterRevisionProposalJSON({
    latestProposalJSON: proposalJSONWithRedirect,
    localProposalJSON: localVersion,
    existingCounterRevisionState: unwrapCachedProposalCounterRevisionState(proposalEntry.localRevision),
    partyRevisionSourceProposalJSON: partyRevisionSourceProposalJSON.length
      ? partyRevisionSourceProposalJSON
      : undefined,
  });
  proposalEntry.localRevision = counterRevisionReconciliation.counterRevisionState;
  if (counterRevisionReconciliation.shouldClearLocalProposalJSON) {
    proposalEntry.localVersion = undefined;
    return proposalJSONWithRedirect;
  }
  if (
    !counterRevisionReconciliation.counterRevisionState &&
    Proposal.saveJSONVersionsAreEqual(localVersion, proposalJSONWithRedirect)
  ) {
    proposalEntry.localVersion = undefined;
  }
  return proposalJSONWithRedirect;
};

const localProposalJSONFromProposal = (
  proposal: Proposal,
): {
  hasLocallyUnsavedRedlineChanges: boolean;
  proposalJSON: LocalProposalJSON;
} => {
  const proposalJSON: LocalProposalJSON = { ...proposal.toJSON() };
  const hasLocallyUnsavedRedlineChanges = Boolean(
    (proposal as Proposal & { hasLocallyUnsavedRedlineChanges?: boolean }).hasLocallyUnsavedRedlineChanges,
  );
  if (hasLocallyUnsavedRedlineChanges) {
    proposalJSON.hasLocallyUnsavedRedlineChanges = true;
  }
  if (proposal.isSubmitted && hasLocallyUnsavedRedlineChanges) {
    const redlinedRevisionJSON = proposal.redlinedRevision?.toJSON();
    if (redlinedRevisionJSON) {
      proposalJSON.partyRevision = redlinedRevisionJSON;
    }
  }

  return { hasLocallyUnsavedRedlineChanges, proposalJSON };
};

const cacheSubmittedLocalProposal = (proposalEntry: DraftProposalStoreEntry, proposalJSON: LocalProposalJSON): void => {
  const counterRevisionReconciliation = ProposalRedlineFactory.reconcileCounterRevisionProposalJSON({
    latestProposalJSON: unwrapCachedProposalJSON(proposalEntry.serverVersion) ?? proposalJSON,
    localProposalJSON: proposalJSON,
    existingCounterRevisionState: unwrapCachedProposalCounterRevisionState(proposalEntry.localRevision),
  });
  proposalEntry.localRevision = counterRevisionReconciliation.counterRevisionState;
  proposalEntry.localVersion = undefined;
};

const cacheDraftLocalProposal = ({
  entriesById,
  hasLocallyUnsavedRedlineChanges,
  proposalEntry,
  proposalJSON,
}: {
  entriesById: ProposalEntriesById;
  hasLocallyUnsavedRedlineChanges: boolean;
  proposalEntry: DraftProposalStoreEntry;
  proposalJSON: LocalProposalJSON;
}): void => {
  proposalEntry.localRevision = undefined;
  const localVersion = applyLatestRevisionRedirectToProposalJSON(proposalJSON, entriesById);
  const localVersionMatchesServer = Proposal.saveJSONVersionsAreEqual(localVersion, proposalEntry.serverVersion);
  proposalEntry.localVersion = !hasLocallyUnsavedRedlineChanges && localVersionMatchesServer ? undefined : localVersion;
};

const proposalStoreEntryIsEmpty = (proposalEntry: DraftProposalStoreEntry) =>
  !proposalEntry.barebones &&
  !proposalEntry.draftBuilder &&
  !proposalEntry.localRevision &&
  !proposalEntry.localVersion &&
  !proposalEntry.redirects &&
  proposalEntry.serverVersion === undefined;

const clearProposalDraftBuilders = (state: DraftProposalStoreState, proposalIdValue: string) => {
  const proposalEntry = state.byId[proposalIdValue];
  if (!proposalEntry?.draftBuilder) return;

  delete proposalEntry.draftBuilder;
  if (proposalStoreEntryIsEmpty(proposalEntry)) {
    delete state.byId[proposalIdValue];
  }
};

const clearProposalLocalOverrides = (proposalEntry: DraftProposalStoreEntry) => {
  proposalEntry.localRevision = undefined;
  proposalEntry.localVersion = undefined;
};

const cacheFulfilledServerProposal = (
  state: DraftProposalStoreState,
  proposalJSON: ProposalJSON,
  options: { clearDraftBuilders?: boolean } = {},
): void => {
  if (!proposalJSON.id) {
    console.error("Proposal does not have an id", proposalJSON);
    return;
  }

  if (options.clearDraftBuilders) {
    clearProposalDraftBuilders(state, proposalJSON.id);
  }
  cacheProposalServerVersion(state, proposalJSON);
  clearProposalLocalOverrides(getOrCreateProposalEntry(state, proposalJSON.id));
};

const markProposalDeleted = (state: DraftProposalStoreState, proposalIdValue: string | undefined): void => {
  if (!proposalIdValue) {
    console.error("Proposal does not have an id", proposalIdValue);
    return;
  }

  const proposalEntry = getOrCreateProposalEntry(state, proposalIdValue);
  proposalEntry.serverVersion = null;
  clearProposalLocalOverrides(proposalEntry);
};

const updateDraftBuilderCacheFromIncomingProposal = (
  state: DraftProposalStoreState,
  incomingProposal: Proposal,
  incomingProposalJSON: ProposalJSON,
  existingServerProposalJSON: ProposalJSON | null | undefined,
  session: Session,
): void => {
  if (incomingProposal.isSubmitted || !incomingProposal.id) return;

  const proposalEntry = state.byId[incomingProposal.id.value];
  const existingUpdatedBuilder = ProposalBuilder.fromJSON({
    json: unwrapCachedProposalBuilderJSON(proposalEntry?.draftBuilder?.updated),
    session,
  });
  const existingOriginalBuilder = ProposalBuilder.fromJSON({
    json: unwrapCachedProposalBuilderJSON(proposalEntry?.draftBuilder?.original),
    session,
  });
  if (!existingUpdatedBuilder && !existingOriginalBuilder) return;

  const shouldRebaseBuilder = incomingProposalRequiresDraftBuilderRebase(
    existingServerProposalJSON,
    incomingProposalJSON,
  );
  const updatedBuilder =
    existingUpdatedBuilder && shouldRebaseBuilder
      ? existingUpdatedBuilder.mergeUpdatedSpec(incomingProposal.spec)
      : existingUpdatedBuilder;
  const originalBuilder = shouldRebaseBuilder
    ? new ProposalBuilder({ session, startingSpec: incomingProposal.spec })
    : existingOriginalBuilder;

  const draftBuilderEntry = getOrCreateDraftBuilderEntry(state, incomingProposal.id.value);
  draftBuilderEntry.original = originalBuilder?.toJSON();
  draftBuilderEntry.updated = updatedBuilder?.toJSON();
};

const getLatestRevisionRedirectForProposal = (
  proposalJSON: ProposalJSON,
  entriesById: ProposalEntriesById,
): LatestRevisionRedirect | undefined => {
  const directRedirect = proposalJSON.id ? entriesById[proposalJSON.id]?.redirects?.latestRevision : undefined;
  if (directRedirect?.supersededProposalId === proposalJSON.id) return directRedirect;

  const latestRevisionRedirect = proposalJSON.latestRevisionId
    ? entriesById[proposalJSON.latestRevisionId]?.redirects?.latestRevision
    : undefined;
  if (latestRevisionRedirect?.previousLatestRevisionId === proposalJSON.latestRevisionId) {
    return latestRevisionRedirect;
  }

  return undefined;
};

const applyLatestRevisionRedirectToProposalJSON = (
  proposalJSON: ProposalJSON,
  entriesById: ProposalEntriesById,
): ProposalJSON => {
  const latestRevisionRedirect = getLatestRevisionRedirectForProposal(proposalJSON, entriesById);
  if (!latestRevisionRedirect) return proposalJSON;

  const isDirectlySuperseded = proposalJSON.id === latestRevisionRedirect.supersededProposalId;
  const supersededById = isDirectlySuperseded ? latestRevisionRedirect.latestRevisionId : proposalJSON.supersededById;
  if (
    proposalJSON.latestRevisionId === latestRevisionRedirect.latestRevisionId &&
    proposalJSON.supersededById === supersededById
  ) {
    return proposalJSON;
  }

  return {
    ...proposalJSON,
    latestRevisionId: latestRevisionRedirect.latestRevisionId,
    supersededById,
  };
};

const proposalsSlice = createSlice({
  name: "proposals",
  initialState,
  reducers: {
    updateServerProposal: (state, action: PayloadAction<ProposalJSON>) => {
      const proposalJSON = action.payload;
      if (!proposalJSON.id) {
        console.error("Proposal does not have an id");
        return;
      }
      cacheProposalServerVersion(state, proposalJSON);
      clearProposalQueryResponses(state);
    },
    recordLatestRevisionRedirect: (state, action: PayloadAction<LatestRevisionRedirect>) => {
      const latestRevisionRedirect = action.payload;
      if (!latestRevisionRedirect.previousLatestRevisionId || !latestRevisionRedirect.latestRevisionId) return;

      getOrCreateProposalEntry(state, latestRevisionRedirect.previousLatestRevisionId).redirects = {
        latestRevision: latestRevisionRedirect,
      };
      if (latestRevisionRedirect.supersededProposalId) {
        getOrCreateProposalEntry(state, latestRevisionRedirect.supersededProposalId).redirects = {
          latestRevision: latestRevisionRedirect,
        };
      }

      for (const proposalEntry of Object.values(state.byId)) {
        if (!proposalEntry) continue;
        if (proposalEntry.serverVersion) {
          proposalEntry.serverVersion = applyLatestRevisionRedirectToProposalJSON(
            proposalEntry.serverVersion,
            state.byId,
          );
        }
        if (proposalEntry.localVersion) {
          proposalEntry.localVersion = applyLatestRevisionRedirectToProposalJSON(
            proposalEntry.localVersion,
            state.byId,
          );
        }
      }
      clearProposalQueryResponses(state);
    },
    updateLocalProposal: (state, action: PayloadAction<Proposal>) => {
      const proposal = action.payload;
      if (!proposal.id) {
        console.error("Proposal does not have an id");
        return;
      }
      const { hasLocallyUnsavedRedlineChanges, proposalJSON } = localProposalJSONFromProposal(proposal);
      const proposalEntry = getOrCreateProposalEntry(state, proposal.id.value);
      if (proposal.isSubmitted) {
        cacheSubmittedLocalProposal(proposalEntry, proposalJSON);
        return;
      }

      cacheDraftLocalProposal({
        entriesById: state.byId,
        hasLocallyUnsavedRedlineChanges,
        proposalEntry,
        proposalJSON,
      });
    },
    clearLocalProposalById: (state, action: PayloadAction<Guid>) => {
      const proposalEntry = state.byId[action.payload.value];
      if (!proposalEntry) return;

      clearProposalLocalOverrides(proposalEntry);
      if (proposalStoreEntryIsEmpty(proposalEntry)) {
        delete state.byId[action.payload.value];
      }
    },
    removeProposal: (state, action: PayloadAction<Guid>) => {
      const proposalEntry = getOrCreateProposalEntry(state, action.payload.value);
      proposalEntry.serverVersion = null;
      clearProposalLocalOverrides(proposalEntry);
      clearProposalDraftBuilders(state, action.payload.value);
    },
    /**
     * The proposal is gone for this viewer — deleted, or their access revoked — so anyone who has it
     * open must be told and sent back. Separate from `removeProposal`, which only empties the caches: a
     * viewer already inside the editor renders from the canonical negotiation envelope, which keeps its
     * last good projection, so clearing caches alone left a reviewer editing a deleted proposal.
     */
    recordProposalNoLongerAvailable: (state, action: PayloadAction<Guid>) => {
      getOrCreateProposalEntry(state, action.payload.value).isNoLongerAvailable = true;
    },
    clearNewProposalBuilder: (state) => {
      clearProposalDraftBuilders(state, Guid.EMPTY.value);
    },
    clearProposalBuilderByProposalId: (state, action: PayloadAction<Guid>) => {
      clearProposalDraftBuilders(state, action.payload.value);
    },
    promoteCreatedProposalDraft: (
      state,
      action: PayloadAction<{
        proposal: ProposalJSON;
        builder: ProposalBuilder;
      }>,
    ) => {
      const { proposal, builder } = action.payload;
      if (!proposal.id) {
        console.error("Proposal does not have an id");
        return;
      }

      cacheProposalServerVersion(state, proposal);
      clearProposalLocalOverrides(getOrCreateProposalEntry(state, proposal.id));
      const persistedDraftBuilder = getOrCreateDraftBuilderEntry(state, proposal.id);
      // Both snapshots intentionally start from the saved response. New-draft
      // history describes initial creation and must not become reviewer-facing
      // auto-comments after the POST establishes the persisted baseline.
      persistedDraftBuilder.original = builder.toJSON();
      persistedDraftBuilder.updated = builder.toJSON();
      clearProposalDraftBuilders(state, Guid.EMPTY.value);
      clearProposalQueryResponses(state);
    },
    replaceUpdatedProposalBuilder: (state, action: PayloadAction<{ proposalId: Guid; builder?: ProposalBuilder }>) => {
      const proposalIdValue = action.payload.proposalId.value;
      const builderJSON = action.payload.builder?.toJSON();
      getOrCreateDraftBuilderEntry(state, proposalIdValue).updated = builderJSON;
    },
    replaceOriginalProposalBuilder: (
      state,
      action: PayloadAction<{
        proposalId: Guid;
        builder: ProposalBuilder | undefined;
      }>,
    ) => {
      const proposalIdValue = action.payload.proposalId.value;
      const builderJSON = action.payload.builder?.toJSON();
      getOrCreateDraftBuilderEntry(state, proposalIdValue).original = builderJSON;
    },
    resetBuilderByProposalId: (
      state,
      action: PayloadAction<{
        id: Guid;
        session: Session;
      }>,
    ) => {
      const { id } = action.payload;
      const json = state.byId[id.value]?.draftBuilder?.updated;
      if (!json) return;
      getOrCreateDraftBuilderEntry(state, id.value).updated = state.byId[id.value]?.draftBuilder?.original;
    },
    updateProposalInterfaceQuery: (
      state,
      action: PayloadAction<{
        interfaceName: string;
        query: ProposalQuery;
      }>,
    ) => {
      const { interfaceName, query } = action.payload;
      removeInterfaceFromQueryEntries(state, interfaceName);
      const queryEntry = getOrCreateQueryEntry(state, query.cacheKey);
      queryEntry.query = ProposalQuerySchema.serialize(query);
      addInterfaceToQueryEntry(queryEntry, interfaceName);
    },
    resetProposalSessionByProposalId: (
      state,
      action: PayloadAction<{
        id: Guid;
      }>,
    ) => {
      const { id } = action.payload;
      getOrCreateDraftBuilderEntry(state, id.value).updated = state.byId[id.value]?.draftBuilder?.original;
    },
    cleanupDeletedDocumentReferences: (state, action: PayloadAction<Guid>) => {
      const documentId = action.payload.value;

      for (const proposalEntry of Object.values(state.byId)) {
        if (!proposalEntry) continue;
        if (proposalEntry.serverVersion) {
          const clonedServerVersion = cloneProposalJSON(proposalEntry.serverVersion);
          if (cleanupProposalJSONForDeletedDocument(clonedServerVersion, documentId)) {
            proposalEntry.serverVersion = clonedServerVersion;
          }
        }
        if (proposalEntry.localVersion) {
          const clonedLocalVersion = cloneProposalJSON(proposalEntry.localVersion);
          if (cleanupProposalJSONForDeletedDocument(clonedLocalVersion, documentId)) {
            proposalEntry.localVersion = clonedLocalVersion;
          }
        }
        if (proposalEntry.draftBuilder?.original) {
          const clonedOriginalBuilder = cloneProposalBuilderJSON(proposalEntry.draftBuilder.original);
          if (cleanupProposalBuilderJSONForDeletedDocument(clonedOriginalBuilder, documentId)) {
            proposalEntry.draftBuilder.original = clonedOriginalBuilder;
          }
        }
        if (proposalEntry.draftBuilder?.updated) {
          const clonedUpdatedBuilder = cloneProposalBuilderJSON(proposalEntry.draftBuilder.updated);
          if (cleanupProposalBuilderJSONForDeletedDocument(clonedUpdatedBuilder, documentId)) {
            proposalEntry.draftBuilder.updated = clonedUpdatedBuilder;
          }
        }
      }

      for (const queryEntry of Object.values(state.byQuery)) {
        if (!queryEntry) continue;
        if (queryEntry.response) {
          queryEntry.response = cleanupProposalResponseForDeletedDocument(queryEntry.response, documentId);
        }
        if (queryEntry.lastSuccessfulResponse) {
          queryEntry.lastSuccessfulResponse = cleanupProposalResponseForDeletedDocument(
            queryEntry.lastSuccessfulResponse,
            documentId,
          );
        }
      }
    },
    hydrateLocalProposalStore: (state, action: PayloadAction<ProposalStoreState>) => {
      const localStoreState = extractProposalLocalStoreState(action.payload);

      for (const [proposalIdValue, proposalEntry] of Object.entries(localStoreState.byId)) {
        if (!proposalEntry) continue;

        const existingProposalEntry = getOrCreateProposalEntry(state, proposalIdValue);
        if (proposalEntry.localVersion) {
          existingProposalEntry.localVersion = applyLatestRevisionRedirectToProposalJSON(
            proposalEntry.localVersion,
            state.byId,
          );
        }
        if (proposalEntry.localRevision) {
          existingProposalEntry.localRevision = proposalEntry.localRevision;
        }
        if (proposalEntry.draftBuilder) {
          existingProposalEntry.draftBuilder = {
            original: proposalEntry.draftBuilder.original,
            updated: proposalEntry.draftBuilder.updated,
          };
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(proposalsApi.endpoints.getProposalsByIds.matchFulfilled, (state, action) => {
      for (const proposal of action.payload) {
        if (!proposal.id) {
          console.error("Proposal does not have an id", proposal);
          continue;
        }
        getOrCreateProposalEntry(state, proposal.id).barebones = proposal;
      }
    });

    builder.addMatcher(proposalsApi.endpoints.submitProposal.matchFulfilled, (state, action) => {
      cacheFulfilledServerProposal(state, action.payload, {
        clearDraftBuilders: true,
      });
    });

    builder.addMatcher(proposalsApi.endpoints.requestProposalRevision.matchFulfilled, (state, action) => {
      cacheFulfilledServerProposal(state, action.payload, {
        clearDraftBuilders: true,
      });
    });

    builder.addMatcher(proposalsApi.endpoints.approveProposal.matchFulfilled, (state, action) => {
      cacheFulfilledServerProposal(state, action.payload);
    });

    builder.addMatcher(proposalsApi.endpoints.hireProposal.matchFulfilled, (state, action) => {
      cacheFulfilledServerProposal(state, action.payload);
    });

    builder.addMatcher(proposalsApi.endpoints.saveProposal.matchFulfilled, (state, action) => {
      cacheFulfilledServerProposal(state, action.payload, {
        clearDraftBuilders: true,
      });
    });

    builder.addMatcher(proposalsApi.endpoints.rejectProposal.matchFulfilled, (state, action) => {
      cacheFulfilledServerProposal(state, action.payload);
    });

    builder.addMatcher(proposalsApi.endpoints.cancelProposal.matchFulfilled, (state, action) => {
      cacheFulfilledServerProposal(state, action.payload);
    });

    builder.addMatcher(proposalsApi.endpoints.deleteProposal.matchFulfilled, (state, action) => {
      markProposalDeleted(state, action.meta.arg.originalArgs.proposalId);
    });
    builder.addMatcher(proposalsApi.endpoints.bulkDeleteProposals.matchFulfilled, (state, action) => {
      for (const proposalIdValue of action.meta.arg.originalArgs.ids) {
        markProposalDeleted(state, proposalIdValue);
      }
    });

    builder.addMatcher(proposalsApi.endpoints.getProposals.matchFulfilled, (state, action) => {
      if (!action.meta.arg.originalArgs) return;
      const { query, interfaceName } = action.meta.arg.originalArgs;
      const responseJson = action.payload;
      if (!query || !responseJson) return;
      const queryKey = query.cacheKey;

      const validatedProposalData: (ProposalJSON | ProposalLoadIssue)[] = [];

      for (const data of responseJson.data) {
        const proposalJSON: ProposalJSON = data;

        let id = Guid.EMPTY;
        try {
          id = Guid.fromJSON(proposalJSON.id) ?? id;
          if (ProposalSchema.validateJSON(proposalJSON)) {
            validatedProposalData.push(applyLatestRevisionRedirectToProposalJSON(proposalJSON, state.byId));
          }
        } catch (error) {
          validatedProposalData.push({
            id,
            issue: "Error loading Proposal.",
          });
          console.error("Error validating Proposal JSON:", error);
        }
      }

      const paginatedResponse = PaginatedResponse.from<ProposalJSON | ProposalLoadIssue>(
        validatedProposalData,
        responseJson.headers,
      );

      const queryEntry = getOrCreateQueryEntry(state, queryKey);
      queryEntry.response = paginatedResponse;
      if (interfaceName) {
        removeInterfaceFromQueryEntries(state, interfaceName);
        queryEntry.query = ProposalQuerySchema.serialize(query);
        queryEntry.lastSuccessfulResponse = paginatedResponse;
        addInterfaceToQueryEntry(queryEntry, interfaceName);
      }

      const session = Session.loadFromStorage(() => {});
      for (const proposalData of paginatedResponse.data) {
        if (!proposalData.id) {
          console.error("Failed to deserialize incoming proposal", proposalData);
          continue;
        }
        if (isProposalLoadIssue(proposalData)) {
          continue;
        }
        const incomingProposal: Proposal | null | undefined = Proposal.fromJSON({
          json: proposalData,
          currentUser: session.user!,
          options: { withRedline: true },
        });
        if (!incomingProposal?.id) {
          console.error("Failed to deserialize incoming proposal", proposalData);
          continue;
        }

        const existingProposalJSON = state.byId[incomingProposal.id.value]?.serverVersion;
        if (proposalJSONLastUpdatedIsAfter(existingProposalJSON, incomingProposal.lastUpdated)) {
          continue;
        }

        cacheProposalServerVersion(state, proposalData);
        updateDraftBuilderCacheFromIncomingProposal(
          state,
          incomingProposal,
          proposalData,
          existingProposalJSON,
          session,
        );
      }
    });

    builder.addMatcher(proposalsApi.endpoints.getProposalById.matchFulfilled, (state, action) => {
      if (action.payload === null) {
        const proposalIdValue = action.meta.arg.originalArgs.proposalId;
        const proposalEntry = getOrCreateProposalEntry(state, proposalIdValue);
        proposalEntry.serverVersion = null;
        clearProposalLocalOverrides(proposalEntry);
        return;
      }
      const session: Session = action.meta.arg.originalArgs.session;
      const incomingProposalJSON: ProposalJSON = action.payload;
      const incomingProposal: Proposal | null | undefined = Proposal.fromJSON({
        json: incomingProposalJSON,
        currentUser: session.user!,
        options: { withRedline: true },
      });
      if (!incomingProposal?.id) {
        console.error("Failed to deserialize incoming proposal", incomingProposalJSON);
        return;
      }
      const existingProposalJSON = state.byId[incomingProposal.id.value]?.serverVersion;
      if (proposalJSONLastUpdatedIsAfter(existingProposalJSON, incomingProposal.lastUpdated)) {
        return;
      }
      cacheProposalServerVersion(state, incomingProposalJSON);
      updateDraftBuilderCacheFromIncomingProposal(
        state,
        incomingProposal,
        incomingProposalJSON,
        existingProposalJSON,
        session,
      );
    });
  },
});

export const {
  updateServerProposal,
  recordLatestRevisionRedirect,
  updateLocalProposal,
  clearLocalProposalById,
  removeProposal,
  recordProposalNoLongerAvailable,
  clearNewProposalBuilder,
  clearProposalBuilderByProposalId,
  promoteCreatedProposalDraft,
  replaceUpdatedProposalBuilder,
  replaceOriginalProposalBuilder,
  resetBuilderByProposalId,
  resetProposalSessionByProposalId,
  updateProposalInterfaceQuery,
  cleanupDeletedDocumentReferences,
  hydrateLocalProposalStore,
} = proposalsSlice.actions;

const selectServerProposalIdValue = createSelector(
  [
    (state: { proposals: ProposalStoreState }, idValue: string | undefined) =>
      state.proposals.byId[idValue ?? ""]?.serverVersion,
    (state: { proposals: ProposalStoreState }, _idValue: string | undefined) => state.proposals.byId,
  ],
  (
    proposalEntryJSON: ProposalJSON | null | undefined,
    entriesById: Record<string, ProposalStoreEntry | undefined>,
  ): ProposalJSON | undefined => {
    if (!proposalEntryJSON) return;
    return applyLatestRevisionRedirectToProposalJSON(proposalEntryJSON, entriesById);
  },
);
export const getServerProposalById = (id: Guid | undefined) => (state: { proposals: ProposalStoreState }) =>
  selectServerProposalIdValue(state, id?.value);

/** Whether this proposal has been deleted, or this viewer's access to it revoked, while they had it open. */
export const getProposalIsNoLongerAvailable =
  (id: Guid | undefined) =>
  (state: { proposals: ProposalStoreState }): boolean =>
    id ? Boolean(state.proposals.byId[id.value]?.isNoLongerAvailable) : false;

const selectLocalProposalJSON = (
  localProposalJSON: ProposalJSON | undefined,
  localCounterRevisionState: ProposalCounterRevisionState | undefined,
  serverProposalJSON: ProposalJSON | null | undefined,
  entriesById: ProposalEntriesById,
): ProposalJSON | undefined => {
  const localProposalJSONWithRedirect = localProposalJSON
    ? applyLatestRevisionRedirectToProposalJSON(localProposalJSON, entriesById)
    : undefined;
  const serverProposalJSONWithRedirect = serverProposalJSON
    ? applyLatestRevisionRedirectToProposalJSON(serverProposalJSON, entriesById)
    : undefined;
  const proposalJSONWithRedirect = serverProposalJSONWithRedirect ?? localProposalJSONWithRedirect;
  if (!proposalJSONWithRedirect) return undefined;

  const { localProposalJSON: reconciledLocalProposalJSON } =
    ProposalRedlineFactory.reconcileCounterRevisionProposalJSON({
      latestProposalJSON: serverProposalJSONWithRedirect ?? proposalJSONWithRedirect,
      localProposalJSON: localProposalJSONWithRedirect,
      existingCounterRevisionState: localCounterRevisionState,
      partyRevisionSourceProposalJSON: getPartyRevisionRebaseSources(proposalJSONWithRedirect, entriesById),
    });

  return reconciledLocalProposalJSON;
};

const getLatestRevisionEntryForRedirectedProposalId = (
  idValue: string | undefined,
  entriesById: ProposalEntriesById,
):
  | {
      entry: ProposalStoreEntry | DraftProposalStoreEntry;
      redirect: LatestRevisionRedirect;
    }
  | undefined => {
  if (!idValue) return undefined;

  const redirect = entriesById[idValue]?.redirects?.latestRevision;
  if (!redirect?.latestRevisionId || redirect.latestRevisionId === idValue) return undefined;

  const latestEntry = entriesById[redirect.latestRevisionId];
  return latestEntry ? { entry: latestEntry, redirect } : undefined;
};

const markLocalProposalJSONAsRedirectedFromPreviousRoute = (
  proposalJSON: ProposalJSON,
  redirect: LatestRevisionRedirect,
): ProposalJSON => {
  const redirectedProposalJSON = cloneProposalJSON(proposalJSON);
  redirectedProposalJSON.latestRevisionId = redirect.latestRevisionId;
  redirectedProposalJSON.supersededById = redirect.latestRevisionId;
  return redirectedProposalJSON;
};

const selectLocalProposalIdValue = createSelector(
  [
    (_state: { proposals: ProposalStoreState }, idValue: string | undefined) => idValue,
    (state: { proposals: ProposalStoreState }, idValue: string | undefined) =>
      state.proposals.byId[idValue ?? ""]?.localVersion,
    (state: { proposals: ProposalStoreState }, idValue: string | undefined) =>
      state.proposals.byId[idValue ?? ""]?.localRevision,
    (state: { proposals: ProposalStoreState }, idValue: string | undefined) =>
      state.proposals.byId[idValue ?? ""]?.serverVersion,
    (state: { proposals: ProposalStoreState }, _idValue: string | undefined) => state.proposals.byId,
  ],
  (
    idValue: string | undefined,
    proposalEntryJSON: ProposalJSON | undefined,
    localCounterRevisionState: ProposalCounterRevisionState | undefined,
    serverProposalEntryJSON: ProposalJSON | null | undefined,
    entriesById: Record<string, ProposalStoreEntry | undefined>,
  ): ProposalJSON | undefined => {
    const redirectedLatestEntry = getLatestRevisionEntryForRedirectedProposalId(idValue, entriesById);
    if (redirectedLatestEntry) {
      const redirectedLatestLocalProposalJSON = selectLocalProposalJSON(
        unwrapCachedProposalJSON(redirectedLatestEntry.entry.localVersion),
        unwrapCachedProposalCounterRevisionState(redirectedLatestEntry.entry.localRevision),
        unwrapCachedProposalJSON(redirectedLatestEntry.entry.serverVersion),
        entriesById,
      );
      if (redirectedLatestLocalProposalJSON) {
        return markLocalProposalJSONAsRedirectedFromPreviousRoute(
          redirectedLatestLocalProposalJSON,
          redirectedLatestEntry.redirect,
        );
      }
    }

    return selectLocalProposalJSON(proposalEntryJSON, localCounterRevisionState, serverProposalEntryJSON, entriesById);
  },
);

export const getLocalProposalById = (id: Guid | undefined) => (state: { proposals: ProposalStoreState }) =>
  selectLocalProposalIdValue(state, id?.value);

export const getEffectiveProposalById = (id: Guid | undefined) => (state: { proposals: ProposalStoreState }) =>
  selectLocalProposalIdValue(state, id?.value) ?? selectServerProposalIdValue(state, id?.value);

const selectOriginalBuilderByProposalId = createSelector(
  [
    (state: { proposals: ProposalStoreState }) => state.proposals.byId,
    (_: { proposals: ProposalStoreState }, proposalId?: Guid) => proposalId,
  ],
  (entriesById, proposalId) => {
    if (!proposalId) return;
    const builderJson = entriesById[proposalId.value]?.draftBuilder?.original;
    if (!builderJson) return;
    return builderJson;
  },
);

const selectUpdatedBuilderByProposalId = createSelector(
  [
    (state: { proposals: ProposalStoreState }) => state.proposals.byId,
    (_: { proposals: ProposalStoreState }, proposalId?: Guid) => proposalId,
  ],
  (entriesById, proposalId) => {
    if (!proposalId) return;
    const builderJson = entriesById[proposalId.value]?.draftBuilder?.updated;
    if (!builderJson) return;
    return builderJson;
  },
);

export const getUpdatedProposalBuilderByProposalId =
  (proposalId?: Guid) => (state: { proposals: ProposalStoreState }) =>
    selectUpdatedBuilderByProposalId(state, proposalId);

export const getOriginalProposalBuilderByProposalId =
  (proposalId?: Guid) => (state: { proposals: ProposalStoreState }) =>
    selectOriginalBuilderByProposalId(state, proposalId);

const selectProposalQueryByInterface = createSelector(
  [
    (state: { proposals: ProposalStoreState }) => state.proposals.byQuery,
    (_: { proposals: ProposalStoreState }, interfaceName: string) => interfaceName,
  ],
  (queryEntries, interfaceName) => {
    const queryJson = Object.values(queryEntries).find((queryEntry) =>
      queryEntry?.interfaces.includes(interfaceName),
    )?.query;
    if (!queryJson) return;
    return ProposalQuery.fromJSON(queryJson);
  },
);
export const getProposalQueryByInterface = (interfaceName: string) => (state: { proposals: ProposalStoreState }) =>
  selectProposalQueryByInterface(state, interfaceName);

const selectProposalsByQueryKey = createSelector(
  [
    (state: { proposals: ProposalStoreState }, queryKey: string | undefined) =>
      state.proposals.byQuery[queryKey ?? ""]?.response,
    (_state: { proposals: ProposalStoreState }, _queryKey: string | undefined, query: ProposalQuery | undefined) =>
      query,
  ],
  (
    paginatedProposalJSONEntry: PaginatedResponse<ProposalJSON | ProposalLoadIssue> | undefined,
  ): PaginatedResponse<ProposalJSON | ProposalLoadIssue> | undefined => {
    if (!paginatedProposalJSONEntry) return;
    return paginatedProposalJSONEntry;
  },
);

export const getProposalsByQuery = (query: ProposalQuery | undefined) => (state: { proposals: ProposalStoreState }) =>
  selectProposalsByQueryKey(state, query?.cacheKey, query);

const selectLastSuccessfulProposalQueryByInterface = createSelector(
  [
    (state: { proposals: ProposalStoreState }, _interfaceName: string) => state.proposals.byQuery,
    (_state: { proposals: ProposalStoreState }, interfaceName: string) => interfaceName,
    (_state: { proposals: ProposalStoreState }, _interfaceName: string) => undefined,
  ],
  (
    queryEntries: Record<string, ProposalQueryEntry | undefined>,
    interfaceName: string,
  ): PaginatedResponse<ProposalJSON | ProposalLoadIssue> | undefined => {
    const paginatedProposalJSONEntry = Object.values(queryEntries).find((queryEntry) =>
      queryEntry?.interfaces.includes(interfaceName),
    )?.lastSuccessfulResponse;
    if (!paginatedProposalJSONEntry) return;
    return paginatedProposalJSONEntry;
  },
);
export const getLastSuccessfulProposalQueryByInterface =
  (interfaceName: string) => (state: { proposals: ProposalStoreState }) => {
    return selectLastSuccessfulProposalQueryByInterface(state, interfaceName);
  };

const selectProposalsByIds = createSelector(
  [
    (state: { proposals: ProposalStoreState }) => state.proposals.byId,
    (_: { proposals: ProposalStoreState }, ids: Guid[]) => ids,
  ],
  (entries, ids) => {
    return ids.map((id) => entries[id.value]?.barebones).filter((proposal) => proposal !== undefined);
  },
);
export const getProposalsByIds = (ids: Guid[]) => (state: { proposals: ProposalStoreState }) => {
  return selectProposalsByIds(state, ids);
};

export type ProposalError = {
  id: Guid;
  error: SerializedError;
};

export default proposalsSlice;
